import type { IWarehouseApi } from "../../../api/WarehouseApi";
import {
  BOX_ID_PATTERN,
  PRODUCT_ID_PATTERN,
  isBoxId,
  isProductId,
} from "../../../domain/entity-ids";
import { Box } from "../../../stores/Box";
import { Product } from "../../../stores/Product";
import { type INavigationStore, Screens } from "../../../stores/navigation";

export { BOX_ID_PATTERN, PRODUCT_ID_PATTERN, isBoxId, isProductId };

function normalizeEntityId(value: string | null | undefined): string {
  return (value || "").trim();
}

interface OpenProductScreenByIdOptions {
  api: IWarehouseApi;
  navigationStore: INavigationStore;
  productId: string;
  onLoaded?: (product: Product) => void;
  onNotFound?: () => void;
  onError?: (error: unknown) => void;
}

interface OpenBoxScreenByIdOptions {
  api: IWarehouseApi;
  navigationStore: INavigationStore;
  boxId: string;
  onLoaded?: (box: Box) => void;
  onNotFound?: () => void;
  onError?: (error: unknown) => void;
}

export async function openProductScreenById(
  options: OpenProductScreenByIdOptions,
): Promise<boolean> {
  const normalizedId = normalizeEntityId(options.productId);
  if (!isProductId(normalizedId)) {
    return false;
  }

  try {
    const product = await options.api.getProductById(normalizedId);
    if (!product) {
      options.onNotFound?.();
      return false;
    }

    const model = new Product(product);
    options.onLoaded?.(model);
    options.navigationStore.setScreen(Screens.Product, model);
    return true;
  } catch (error) {
    options.onError?.(error);
    return false;
  }
}

export async function openBoxScreenById(
  options: OpenBoxScreenByIdOptions,
): Promise<boolean> {
  const normalizedId = normalizeEntityId(options.boxId);
  if (!isBoxId(normalizedId)) {
    return false;
  }

  try {
    const box = await options.api.getBoxById(normalizedId);
    if (!box) {
      options.onNotFound?.();
      return false;
    }

    const model = new Box(box);
    options.onLoaded?.(model);
    options.navigationStore.setScreen(Screens.Box, model);
    return true;
  } catch (error) {
    options.onError?.(error);
    return false;
  }
}
